import { useCallback, useMemo, useState } from 'react';

import type { Comment, Petition } from 'src/app/pages/petition/comments/pageContext';
import { usePetitionCommentsPageData, usePetitionCommentsPageState } from 'src/app/pages/petition/comments/pageContext';
import { useCurrentUser } from 'src/app/shared/hooks/session';

type Result = ModelHookResult<
	{
		petition: Petition;
		showShareActions: boolean;
		canReport: boolean;
		canDelete: boolean;
		trackingProperties: Record<string, string | number | boolean>;
	},
	{
		handleBeforeDeleteComment: () => void;
		handleDeletedComment: () => void;
		handleLikeToggle: (liked: boolean) => void;
	}
>;

export function useCommentFooter(comment: Comment): Result {
	const {
		data: { petition },
	} = usePetitionCommentsPageData();
	const currentUser = useCurrentUser();
	const [viewerLiked, setViewerLiked] = useState(comment.viewerLiked);
	const [, { markCommentAsDeleted, clearDeleteConfirmMessage }] = usePetitionCommentsPageState();

	const { user } = comment;

	return {
		data: {
			petition,
			showShareActions: viewerLiked,
			canReport: currentUser?.id !== user.id,
			canDelete:
				!!currentUser?.roles?.support ||
				!!currentUser?.roles.campaigns ||
				currentUser?.id === comment.user.id ||
				currentUser?.id === petition.user.id,
			trackingProperties: useMemo(
				() => ({
					commentable_type: 'Event',
					commentable_id: petition.id,
					own_petition: petition.user.id === currentUser?.id,
				}),
				[petition, currentUser],
			),
		},
		actions: {
			handleBeforeDeleteComment: useCallback(() => {
				clearDeleteConfirmMessage();
			}, [clearDeleteConfirmMessage]),
			handleDeletedComment: useCallback(() => {
				markCommentAsDeleted(comment.id);
			}, [markCommentAsDeleted, comment.id]),
			handleLikeToggle: useCallback((liked: boolean) => {
				setViewerLiked(liked);
			}, []),
		},
	};
}
